import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { AdminService, BaseComponent, FirmwareInstructionDoc, FirmwareInstructionMode, FirmwareInstructionsService, Language, LanguageService, UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

type LangStatus = "idle" | "translating" | "done" | "error";

/** Shared with product localized descriptions — brand terms the AI must keep verbatim. */
const DO_NOT_TRANSLATE_CONFIG_TYPE = "ai_do_not_translate";
const DEFAULT_DO_NOT_TRANSLATE = "Fender, PreSonus, Squier, Studio One, Notion, AudioBox, FaderPort, Quantum, StudioLive, Eris, Sceptre, ATOM, Revelator, Temblor, PreSonus Sphere";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-firmware-instruction-form",
  templateUrl: "firmware-instruction-form.component.html",
  standalone: false,
  styles: [
    `
      .firmware-content-mode-toggle {
        display: inline-flex;
        padding: 3px;
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.05);
        gap: 2px;
      }
      .firmware-content-mode-toggle button {
        border: 0;
        background: transparent;
        border-radius: 999px;
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 600;
        color: inherit;
        opacity: 0.7;
      }
      .firmware-content-mode-toggle button.active {
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        opacity: 1;
      }
      .firmware-content-mode-toggle button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .firmware-preview-shell {
        margin-top: 12px;
      }
      .firmware-preview-chrome {
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 14px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
      }
      .firmware-preview-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.02), transparent);
      }
      .firmware-preview-header h4 {
        margin: 0;
        font-size: 16px;
      }
      .firmware-preview-kicker {
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        opacity: 0.55;
        margin-bottom: 2px;
      }
      .firmware-preview-langs {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        background: rgba(0, 0, 0, 0.015);
      }
      .firmware-preview-lang-btn {
        border: 1px solid rgba(0, 0, 0, 0.08);
        background: #fff;
        border-radius: 999px;
        padding: 5px 11px;
        font-size: 12px;
        line-height: 1.2;
      }
      .firmware-preview-lang-btn.active {
        border-color: transparent;
        background: #222;
        color: #fff;
      }
      .firmware-preview-lang-btn.missing {
        opacity: 0.55;
      }
      .firmware-preview-body {
        min-height: 220px;
        max-height: 420px;
        overflow: auto;
        padding: 20px 18px 24px;
      }
      .firmware-preview-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        text-align: center;
        opacity: 0.65;
        gap: 8px;
      }
      .firmware-preview-empty fa-icon,
      .firmware-preview-empty .preview-empty-icon {
        font-size: 22px;
        opacity: 0.7;
      }
    `,
  ],
})
export class FirmwareInstructionFormComponent extends BaseComponent implements OnInit {
  @Input() doc: FirmwareInstructionDoc = null;
  @Output() saved = new EventEmitter<FirmwareInstructionDoc>();

  public isNew = true;
  public saving = false;
  public title = "";
  public mode: FirmwareInstructionMode = "update";
  public mainContent = "";
  public translations: Record<string, string> = { en: "" };
  public productIds: number[] = [];
  public associatedProducts: Array<{ id: number; title: string }> = [];

  public modes: FirmwareInstructionMode[] = ["update", "recover", "dante", "faderport-1.35", "faderport-1.38", "option-card-dante"];
  public otherLanguages: Language[] = [];
  public showAllLanguages = false;
  public quillReady = true;

  public translating = false;
  public missingTranslations = false;
  /** Per-locale missing flag for template bindings (avoid method calls in the view). */
  public missingByLang: Record<string, boolean> = {};
  public langStatus: Record<string, LangStatus> = {};
  public translateError: string | null = null;
  public showConfig = false;
  public savingConfig = false;
  public doNotTranslateTerms = DEFAULT_DO_NOT_TRANSLATE;

  public contentViewMode: "edit" | "preview" = "edit";
  public previewLang = "en";
  public previewLangLabel = "English";
  public previewLangOptions: Array<{ code: string; label: string; missing: boolean }> = [];
  public previewHtml = "";
  public previewEmpty = true;

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public appService: AppService,
    private firmwareInstructionsService: FirmwareInstructionsService,
    private languageService: LanguageService,
    private adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.otherLanguages = this.languageService.languages.filter((lang) => lang.code !== "en");
    this.otherLanguages.forEach((lang) => (this.langStatus[lang.code] = "idle"));
    this.loadDoNotTranslateTerms();

    if (this.doc?.id) {
      this.isNew = false;
      this.title = this.doc.title || "";
      this.mode = this.doc.mode || "update";
      this.mainContent = this.doc.mainContent || "";
      this.translations = { en: "", ...(this.doc.translations || {}) };
      this.productIds = [...(this.doc.productIds || [])];
      this.associatedProducts = this.resolveAssociatedProducts(this.doc);
    }
    this.recomputeMissing();
  }

  /** Prefer BE-enriched `products` titles; fall back to Product {id} placeholders. */
  private resolveAssociatedProducts(doc: FirmwareInstructionDoc): Array<{ id: number; title: string }> {
    if (doc.products?.length) {
      return doc.products.map((product) => ({
        id: Number(product.id),
        title: product.title || `Product ${product.id}`,
      }));
    }
    return (doc.productIds || []).map((id) => ({ id, title: `Product ${id}` }));
  }

  addProduct(product: any) {
    const id = Number(product.id);
    if (!id || this.productIds.includes(id)) return;
    this.productIds = [...this.productIds, id];
    this.associatedProducts = [...this.associatedProducts, { id, title: product.title || `Product ${id}` }];
  }

  removeProduct(product: { id: number }) {
    const productId = product?.id;
    if (productId == null) return;
    this.productIds = this.productIds.filter((id) => id !== productId);
    this.associatedProducts = this.associatedProducts.filter((p) => p.id !== productId);
  }

  onTranslationChange(code: string, value: string) {
    this.translations = { ...this.translations, [code]: value };
    this.recomputeMissing();
  }

  setContentViewMode(mode: "edit" | "preview") {
    if (this.translating && mode === "preview") return;
    this.contentViewMode = mode;
    if (mode === "preview") this.syncPreviewState();
  }

  setPreviewLang(code: string) {
    this.previewLang = code;
    this.syncPreviewState();
  }

  recomputeMissing() {
    const next: Record<string, boolean> = {};
    this.otherLanguages.forEach((lang) => {
      next[lang.code] = !this.hasText(this.translations[lang.code]);
    });
    this.missingByLang = next;
    this.missingTranslations = this.otherLanguages.some((lang) => next[lang.code]);
    this.syncPreviewState();
  }

  private syncPreviewState() {
    const options = [{ code: "en", label: "English", missing: !this.hasText(this.translations.en) }];
    this.otherLanguages.forEach((lang) => {
      options.push({
        code: lang.code,
        label: lang.translated_name || lang.name,
        missing: !!this.missingByLang[lang.code],
      });
    });
    this.previewLangOptions = options;
    if (!options.some((opt) => opt.code === this.previewLang)) this.previewLang = "en";
    const selected = options.find((opt) => opt.code === this.previewLang);
    this.previewLangLabel = selected?.label || this.previewLang;
    this.previewHtml = this.translations[this.previewLang] || "";
    this.previewEmpty = !this.hasText(this.previewHtml);
  }

  private hasText(html: string | undefined): boolean {
    if (!html) return false;
    return !!html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private loadDoNotTranslateTerms() {
    this.adminService.get(`config/${DO_NOT_TRANSLATE_CONFIG_TYPE}`).subscribe({
      next: (result: any) => {
        if (Array.isArray(result?.data) && result.data.length) this.doNotTranslateTerms = result.data.join(", ");
      },
      error: () => {
        /* keep brand defaults */
      },
    });
  }

  saveConfig() {
    this.savingConfig = true;
    this.adminService.put(`config/${DO_NOT_TRANSLATE_CONFIG_TYPE}`, { data: this.parseDoNotTranslate() }).subscribe({
      next: () => {
        this.savingConfig = false;
        this.showConfig = false;
      },
      error: (err) => {
        this.savingConfig = false;
        this.appService.alert(this.appService.helperService.retrieveErrorMessage(err) || "Failed to save configuration.");
      },
    });
  }

  private parseDoNotTranslate(): string[] {
    return (this.doNotTranslateTerms || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  /**
   * Fills every non-English locale from the English source via generic `POST /admin/translate`.
   * Does not persist — Save on this form writes translations with the document.
   */
  autoTranslate() {
    if (!this.hasText(this.translations.en)) {
      this.appService.alert("Enter the English content first, then auto-translate.");
      return;
    }
    if (!this.otherLanguages.length || this.translating) return;

    this.translateError = null;
    this.translating = true;
    this.showAllLanguages = false;
    this.showConfig = false;
    this.contentViewMode = "edit";
    const doNotTranslate = this.parseDoNotTranslate();
    const english = this.translations.en;
    let remaining = this.otherLanguages.length;

    this.otherLanguages.forEach((lang) => {
      this.langStatus[lang.code] = "translating";
      this.adminService
        .translate({
          fields: { content: english },
          targetLang: lang.code,
          doNotTranslate,
        })
        .subscribe({
          next: (result) => {
            const translated = result?.content?.[lang.code];
            if (translated) {
              this.translations = { ...this.translations, [lang.code]: translated };
            }
            this.langStatus[lang.code] = "done";
            this.finishLang(--remaining);
          },
          error: () => {
            this.langStatus[lang.code] = "error";
            this.finishLang(--remaining);
          },
        });
    });
  }

  private finishLang(remaining: number) {
    this.recomputeMissing();
    if (remaining > 0) return;
    this.translating = false;
    if (this.otherLanguages.some((lang) => this.langStatus[lang.code] === "error")) {
      this.translateError = "Some languages failed to translate. Retry Auto-translate to fill them in.";
    }
    // Expand other languages only once every locale has content.
    if (!this.missingTranslations) {
      this.showAllLanguages = true;
    }
    // Quill only ingests content on init — remount editors with the new HTML.
    this.quillReady = false;
    setTimeout(() => (this.quillReady = true));
  }

  save() {
    if (this.saving || this.translating) return;
    if (!this.title?.trim()) {
      this.appService.alertError("Title is required.");
      return;
    }
    if (!this.mode) {
      this.appService.alertError("Mode is required.");
      return;
    }
    if (!this.mainContent?.trim()) {
      this.appService.alertError("mainContent is required.");
      return;
    }
    if (!this.hasText(this.translations?.en)) {
      this.appService.alertError("English translation is required.");
      return;
    }

    const payload = {
      title: this.title.trim(),
      mode: this.mode,
      mainContent: this.mainContent.trim(),
      productIds: this.productIds,
      translations: this.translations,
    };

    this.saving = true;
    const request$ = this.isNew ? this.firmwareInstructionsService.create(payload) : this.firmwareInstructionsService.update(this.doc.id, payload);

    request$.subscribe({
      next: (doc) => {
        this.saving = false;
        this.saved.emit(doc);
        this.activeModal.close(doc);
      },
      error: (error) => {
        this.saving = false;
        this.appService.alertError(error);
      },
    });
  }
}
